import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * M9.2：直接操纵类单元格（boolean / rating / color-picker）。
 *
 * 这三类的共同点：**没有"编辑器"这一层**，点单元格本身就是动作。
 * 因此它们在 CellView 的「只读展示」分支之外单独渲染，不进入 editing 状态。
 *
 * 注意：这里**不能用 useSettle** —— 它的 settled 标记只在编辑器卸载时由新实例重置，
 * 而色板是常驻弹层（同一实例反复开关），会导致"第一次之后点了没反应"。
 */

/** 复选框：整格可点（含空白处），勾选框水平居中 */
export function BooleanCell({
  value,
  busy,
  onCommit,
}: {
  value: string
  busy: boolean
  onCommit: (v: string) => void
}) {
  const on = value === 'true'
  return (
    <button
      type="button"
      className="cell-check-hit"
      role="checkbox"
      aria-checked={on}
      aria-label={on ? '取消勾选' : '勾选'}
      disabled={busy}
      onClick={() => onCommit(on ? '' : 'true')}
    >
      <span className={'cell-check' + (on ? ' cell-check--on' : '')}>{on ? '✓' : ''}</span>
    </button>
  )
}

/** 评分：点第 n 颗星设为 n（再点当前值清空）；hover 时点亮到鼠标所在那颗 */
export function RatingCell({
  value,
  busy,
  onCommit,
}: {
  value: string
  busy: boolean
  onCommit: (v: string) => void
}) {
  const max = 5
  const n = Number(value) || 0
  const [hover, setHover] = useState(0)
  const lit = hover > 0 ? hover : n

  return (
    <span
      className="cell-stars"
      role="group"
      aria-label={`评分 ${n}/${max}`}
      onMouseLeave={() => setHover(0)}
    >
      {Array.from({ length: max }, (_, i) => {
        const k = i + 1
        return (
          <button
            key={k}
            type="button"
            className={'cell-star' + (k <= lit ? ' cell-star--on' : '')}
            aria-label={`${k} 星`}
            disabled={busy}
            onMouseEnter={() => setHover(k)}
            onFocus={() => setHover(k)}
            onClick={() => onCommit(k === n ? '' : String(k))}
          >
            ★
          </button>
        )
      })}
    </span>
  )
}

// ===== 颜色工具 =====

const clamp01 = (n: number) => Math.max(0, Math.min(1, n))

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  const digits = m === null ? '579bfc' : (m[1] ?? '579bfc')
  const v = parseInt(digits, 16)
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 }
}

function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return '#' + h(r) + h(g) + h(b)
}

function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return { h, s: max === 0 ? 0 : d / max, v: max }
}

function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const c = v * s
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0
  let g = 0
  let b = 0
  if (hp < 1) [r, g, b] = [c, x, 0]
  else if (hp < 2) [r, g, b] = [x, c, 0]
  else if (hp < 3) [r, g, b] = [0, c, x]
  else if (hp < 4) [r, g, b] = [0, x, c]
  else if (hp < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const m = v - c
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 }
}

/** 统一成 #rrggbb / #rrggbbaa；非法返回 '' */
function normalizeHex(raw: string): string {
  const v = raw.trim().toLowerCase()
  if (/^#[0-9a-f]{3}$/.test(v)) return '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3]
  if (/^#[0-9a-f]{6}$/.test(v) || /^#[0-9a-f]{8}$/.test(v)) return v
  if (/^[0-9a-f]{6}$/.test(v) || /^[0-9a-f]{8}$/.test(v)) return '#' + v
  return ''
}

function splitHex(raw: string): { rgb: string; alpha: number } {
  const c = normalizeHex(raw) === '' ? '#579bfc' : normalizeHex(raw)
  if (c.length === 9) return { rgb: c.slice(0, 7), alpha: parseInt(c.slice(7, 9), 16) }
  return { rgb: c, alpha: 255 }
}

function composeHex(rgb: string, alpha: number): string {
  const a = Math.max(0, Math.min(255, Math.round(alpha)))
  return a >= 255 ? rgb : rgb + a.toString(16).padStart(2, '0')
}

/** monday 的常用色板 */
const PICKER_COLORS = [
  '#579bfc',
  '#00c875',
  '#fdab3d',
  '#e2445c',
  '#a25ddc',
  '#0086c0',
  '#ffcb00',
  '#9cd326',
  '#ff7edb',
  '#784bd1',
  '#bb3354',
  '#c4c4c4',
]

/**
 * 颜色列：圆角色块（居中放大）+ 浮层色板 + **自绘取色器**。
 *
 * 为什么自绘：当前 Chrome 的 `input[type=color]` 不支持 alpha（实测 `'alpha' in input === false`），
 * 且系统取色器弹窗会盖住我们自己的「应用」按钮。所以这里自己画：
 * SV 方块（饱和度/明度）+ 色相条 + 透明度条 + hex 输入，全部在弹层内部。
 *
 * 拖拽安全：色板容器 `draggable=false` 且拦截 mousedown/pointerdown/dragstart；
 * 拖动的鼠标事件都在容器内被 `stopPropagation`，不会触发条目行拖拽。
 */
export function ColorPickerCell({
  value,
  busy,
  onCommit,
}: {
  value: string
  busy: boolean
  onCommit: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(false)
  const init = hexToRgb(splitHex(value).rgb)
  const [hsv, setHsv] = useState(() => rgbToHsv(init.r, init.g, init.b))
  const [alpha, setAlpha] = useState(() => splitHex(value).alpha)
  const [hexText, setHexText] = useState('')
  const anchor = useRef<HTMLSpanElement>(null)
  const svRef = useRef<HTMLDivElement>(null)
  const hueRef = useRef<HTMLDivElement>(null)
  const alphaRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) return
    const r = anchor.current?.getBoundingClientRect()
    if (r !== undefined) setPos({ x: Math.round(r.left), y: Math.round(r.bottom + 6) })
  }, [open])

  const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v)
  const rgbHex = rgbToHex(rgb.r, rgb.g, rgb.b)
  const currentHex = composeHex(rgbHex, alpha)
  // SV 方块上小圆点的位置
  const dotLeft = `${Math.round(hsv.s * 100)}%`
  const dotTop = `${Math.round((1 - hsv.v) * 100)}%`

  // 拖动色相/SV/透明度后，hex 输入框必须跟着更新，否则「应用」会提交旧颜色
  useEffect(() => {
    if (!custom) return
    setHexText(composeHex(rgbHex, alpha))
    // 只在拖动/取色导致 hsv 或 alpha 变化时同步；手动输入 hex 时 hsv 也会变，格式化成规范形式
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsv.h, hsv.s, hsv.v, alpha, custom])
  const openPanel = () => {
    const s = splitHex(value)
    const c = hexToRgb(s.rgb)
    setHsv(rgbToHsv(c.r, c.g, c.b))
    setAlpha(s.alpha)
    setHexText(composeHex(s.rgb, s.alpha))
    setCustom(false)
    setOpen(true)
  }

  const pick = (c: string) => {
    onCommit(c)
    setOpen(false)
    setCustom(false)
  }

  /** 在某个 DOM 元素上按下并拖动，持续回报归一化坐标 (0..1, 0..1) */
  const dragOn = (
    el: HTMLElement | null,
    e: React.MouseEvent,
    onPoint: (nx: number, ny: number) => void,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (el === null) return
    const r = el.getBoundingClientRect()
    const handle = (ev: MouseEvent) => {
      onPoint(clamp01((ev.clientX - r.left) / r.width), clamp01((ev.clientY - r.top) / r.height))
    }
    handle(e.nativeEvent)
    const up = () => {
      window.removeEventListener('mousemove', handle)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', handle)
    window.addEventListener('mouseup', up)
  }

  /** 弹层内一切指针/拖动事件都不许往外冒 */
  const shield = {
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onDragStart: (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    },
    draggable: false,
  } as const

  return (
    <span className="cell-colorpick" ref={anchor}>
      <button
        type="button"
        className="cell-colorhit"
        aria-label={value === '' ? '选择颜色' : `当前颜色 ${value}`}
        title={value === '' ? '选择颜色（点格子空白处也可以）' : value}
        disabled={busy}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <span
          className={'cell-swatch' + (value === '' ? ' cell-swatch--empty' : '')}
          style={value === '' ? undefined : { background: value }}
        />
      </button>
      {open && (
        <>
          <div className="panel-backdrop" onClick={() => setOpen(false)} />
          <div
            className="cell-palette"
            style={{ position: 'fixed', left: pos?.x ?? 0, top: pos?.y ?? 0 }}
            onClick={(e) => e.stopPropagation()}
            {...shield}
          >
            {PICKER_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={'cell-palette__item' + (c === value ? ' cell-palette__item--on' : '')}
                style={{ background: c }}
                aria-label={`选择 ${c}`}
                onClick={() => pick(c)}
              />
            ))}

            {/* 彩虹方块：点开自绘取色器（用户要求恢复这个外观） */}
            <button
              type="button"
              className={'cell-palette__rainbow' + (custom ? ' cell-palette__rainbow--on' : '')}
              title="自定义颜色"
              aria-label="自定义颜色"
              onClick={() => {
                setHexText(composeHex(rgbHex, alpha))
                setCustom((c) => !c)
              }}
            />

            {custom && (
              <div className="cp">
                {/* SV 方块：饱和度（横）× 明度（纵） */}
                <div
                  className="cp__sv"
                  ref={svRef}
                  style={{ background: `hsl(${Math.round(hsv.h)}, 100%, 50%)` }}
                  onMouseDown={(e) =>
                    dragOn(svRef.current, e, (nx, ny) =>
                      setHsv((p) => ({ ...p, s: nx, v: 1 - ny })),
                    )
                  }
                >
                  <div className="cp__sv-white" />
                  <div className="cp__sv-black" />
                  <div className="cp__dot" style={{ left: dotLeft, top: dotTop }} />
                </div>

                {/* 色相条 */}
                <div
                  className="cp__hue"
                  ref={hueRef}
                  onMouseDown={(e) =>
                    dragOn(hueRef.current, e, (nx) => setHsv((p) => ({ ...p, h: nx * 360 })))
                  }
                >
                  <div className="cp__bar-dot" style={{ left: `${(hsv.h / 360) * 100}%` }} />
                </div>

                {/* 透明度条（棋盘格 + 渐变） */}
                <div
                  className="cp__alpha"
                  ref={alphaRef}
                  onMouseDown={(e) =>
                    dragOn(alphaRef.current, e, (nx) => setAlpha(Math.round(nx * 255)))
                  }
                >
                  <div
                    className="cp__alpha-grad"
                    style={{
                      backgroundImage: `linear-gradient(to right, rgba(${Math.round(rgb.r)}, ${Math.round(
                        rgb.g,
                      )}, ${Math.round(rgb.b)}, 0), ${rgbHex})`,
                    }}
                  />
                  <div className="cp__bar-dot" style={{ left: `${(alpha / 255) * 100}%` }} />
                </div>

                <div className="cp__foot">
                  <input
                    className="cp__hex"
                    value={hexText}
                    placeholder="#rrggbb / #rrggbbaa"
                    aria-label="十六进制颜色"
                    onChange={(e) => {
                      setHexText(e.target.value)
                      const n = normalizeHex(e.target.value)
                      if (n !== '') {
                        const s = splitHex(n)
                        const c = hexToRgb(s.rgb)
                        setHsv(rgbToHsv(c.r, c.g, c.b))
                        setAlpha(s.alpha)
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="cp__apply"
                    onClick={() => {
                      const n = normalizeHex(hexText)
                      pick(n === '' ? currentHex : n)
                    }}
                  >
                    应用
                  </button>
                </div>
              </div>
            )}

            <button type="button" className="cell-palette__clear" onClick={() => pick('')}>
              清除
            </button>
          </div>
        </>
      )}
    </span>
  )
}
